import { homeOf, isSolved, rowsOf, sameCell, tileCount } from './board';
import { units } from './groups';
import { applyMove } from './move';
import { canRotate } from './mods';
import type { Board, Cell, Move } from './types';

export type Hint = { move: Move } | { place: { tile: number; to: Cell } } | { rotate: { tile: number } } | null;

export function findHint(board: Board): Hint {
  if (isSolved(board)) return null;
  const { n } = board;
  const rows = rowsOf(board);
  // 旋转块已经在原位：提示"点它转正"（最小 tileId 优先）
  for (let t = 0; t < tileCount(board); t++) {
    if (canRotate(board, t) && sameCell(board.pos[t], homeOf(t, n))) return { rotate: { tile: t } };
  }
  // 走法提示不许把任何块挪出它的原位：这样每一步提示都让 (在原位的块数, 正确边数, 剩余旋转次数) 严格变好，
  // 照着提示走一定能走完（不会出现"放回原位 ↔ 粘回去"来回打转）。放回原位的提示只会增加在原位的块
  const homeCount = (b: Board) => b.pos.reduce((k, c, t) => k + (sameCell(c, homeOf(t, n)) ? 1 : 0), 0);
  const home0 = homeCount(board);
  let best: { added: number; move: Move } | null = null;
  // units 已按锚点（单位内最小 tileId）升序；行列按阅读顺序 → 只在严格更优时替换即满足平局规则
  for (const unit of units(board)) {
    const anchor = unit[0];
    for (let row = 0; row < rows; row++) {
      for (let col = 0; col < n; col++) {
        const move: Move = { anchor, to: { row, col } };
        const { events, board: next } = applyMove(board, move);
        if (events.some((e) => e.type === 'invalid')) continue;
        let added = 0;
        let broken = 0;
        for (const e of events) {
          if (e.type === 'merged') added = e.newEdges.length;
          else if (e.type === 'split') broken = e.brokenEdges.length;
        }
        if (added > 0 && added > broken && (!best || added > best.added) && homeCount(next) >= home0) best = { added, move };
      }
    }
  }
  if (best) return { move: best.move };
  // 在原位的块都已转正、翻开（原位的背面块会自动翻开），所以没解开时一定还有不在原位的块
  const tile = board.pos.findIndex((c, t) => !sameCell(c, homeOf(t, n)));
  if (tile < 0) return null;
  return { place: { tile, to: homeOf(tile, n) } };
}
